import {
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToast,
    IonToolbar
} from "@ionic/react";

import {
    addOutline,
    closeOutline,
    removeOutline,
    saveOutline
} from "ionicons/icons";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    fantasyApiGet,
    fantasyApiPost
} from "../services/fantasyApi";

import "./FantasyScheduleSettings.css";

interface FantasyScheduleSlot {
    slot: string;
    teamId: string;
    teamName: string;
}

interface FantasyScheduleGame {
    id: string;
    localSlot: string;
    visitanteSlot: string;
}

interface FantasyScheduleLeague {
    id: string;
    name: string;
    configuredTeams: number;
    availableWeeks: number;
    firstRealWeek: number;
    playoffs: number;
    playoffWeeks: number;
    regularWeeks: number;
    firstEditableWeek: number;
}

interface FantasyScheduleResponse {
    success?: boolean;
    message?: string;
    league?: FantasyScheduleLeague;
    slots?: FantasyScheduleSlot[];
    gamesByWeek?: Record<string, FantasyScheduleGame[]>;
    byesByWeek?: Record<string, string[]>;
}

const playoffOptions = [0, 2, 4];

function getPlayoffWeeks(playoffs: number): number {
    const playoffWeeksByFormat: Record<number, number> = {
        0: 0,
        2: 1,
        4: 2
    };

    return playoffWeeksByFormat[playoffs] ?? -1;
}

function createTemporaryGameId(): string {
    return [
        "new_game",
        Date.now().toString(36),
        Math.random().toString(36).slice(2, 10)
    ].join("_");
}

function getWeekGames(
    gamesByWeek: Record<string, FantasyScheduleGame[]>,
    week: number
): FantasyScheduleGame[] {
    return gamesByWeek[String(week)] || [];
}

function getWeekByes(
    byesByWeek: Record<string, string[]>,
    week: number
): string[] {
    return byesByWeek[String(week)] || [];
}

const FantasyScheduleSettings: React.FC = () => {
    const history = useHistory();
    const { id } = useParams<{ id: string }>();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [toastMessage, setToastMessage] = useState("");

    const [league, setLeague] =
        useState<FantasyScheduleLeague | null>(null);

    const [slots, setSlots] = useState<
        FantasyScheduleSlot[]
    >([]);

    const [playoffs, setPlayoffs] = useState(0);
    const [regularWeeks, setRegularWeeks] = useState(1);

    const [gamesByWeek, setGamesByWeek] = useState<
        Record<string, FantasyScheduleGame[]>
    >({});

    const [byesByWeek, setByesByWeek] = useState<
        Record<string, string[]>
    >({});

    function goBack() {
        history.replace(`/fantasySettingsMenu/${id}`);
    }

    const playoffWeeks = getPlayoffWeeks(playoffs);

    const maximumRegularWeeks = useMemo(
        function () {
            if (!league || playoffWeeks < 0) {
                return 1;
            }

            return Math.max(
                1,
                league.availableWeeks - playoffWeeks
            );
        },
        [league, playoffWeeks]
    );

    const regularWeekOptions = useMemo(
        function () {
            const options: number[] = [];

            for (
                let week = 1;
                week <= maximumRegularWeeks;
                week++
            ) {
                options.push(week);
            }

            return options;
        },
        [maximumRegularWeeks]
    );

    const loadSchedule = useCallback(
        async function () {
            try {
                setLoading(true);
                setError("");

const result = await fantasyApiGet<
    FantasyScheduleResponse
>(
    "control_FantasySchedule.php" +
        `?id=${encodeURIComponent(id)}` +
        `&t=${Date.now()}`
);

if (!result.league) {
    throw new Error(
        result.message ||
            "No fue posible cargar el calendario."
    );
}

                setLeague(result.league);
                setSlots(
                    Array.isArray(result.slots)
                        ? result.slots
                        : []
                );

                setPlayoffs(result.league.playoffs);
                setRegularWeeks(result.league.regularWeeks);

                setGamesByWeek(
                    result.gamesByWeek || {}
                );

                setByesByWeek(
                    result.byesByWeek || {}
                );
            } catch (loadError) {
                console.error(
                    "No fue posible cargar el calendario:",
                    loadError
                );

                setError(
                    loadError instanceof Error
                        ? loadError.message
                        : "No fue posible cargar el calendario."
                );
            } finally {
                setLoading(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadSchedule();
        },
        [loadSchedule]
    );

    function isWeekEditable(week: number): boolean {
        if (!league) {
            return false;
        }

        return week >= league.firstEditableWeek;
    }

    function getUsedSlotsInWeek(week: number): string[] {
        const usedSlots: string[] = [];

        getWeekGames(gamesByWeek, week).forEach(
            function (game) {
                usedSlots.push(
                    game.localSlot,
                    game.visitanteSlot
                );
            }
        );

        return usedSlots;
    }

    function changePlayoffs(value: unknown) {
        const nextPlayoffs = Number(value);

        if (!playoffOptions.includes(nextPlayoffs)) {
            return;
        }

        const nextPlayoffWeeks = getPlayoffWeeks(
            nextPlayoffs
        );

        if (!league || nextPlayoffWeeks < 0) {
            return;
        }

        const nextMaximumRegularWeeks = Math.max(
            1,
            league.availableWeeks - nextPlayoffWeeks
        );

        setPlayoffs(nextPlayoffs);

        setRegularWeeks(function (currentWeeks) {
            return Math.min(
                currentWeeks,
                nextMaximumRegularWeeks
            );
        });
    }

    function changeRegularWeeks(value: unknown) {
        const nextRegularWeeks = Number(value);

        if (
            !Number.isInteger(nextRegularWeeks) ||
            nextRegularWeeks < 1 ||
            nextRegularWeeks > maximumRegularWeeks
        ) {
            return;
        }

        if (
            league &&
            nextRegularWeeks < league.firstEditableWeek
        ) {
            setToastMessage(
                "No puedes eliminar jornadas que ya iniciaron o tienen resultados."
            );

            return;
        }

        setRegularWeeks(nextRegularWeeks);

        setGamesByWeek(function (currentGames) {
            const nextGames: Record<
                string,
                FantasyScheduleGame[]
            > = {};

            Object.keys(currentGames).forEach(function (weekKey) {
                if (Number(weekKey) < nextRegularWeeks) {
                    nextGames[weekKey] = currentGames[weekKey];
                }
            });

            for (
                let week = 0;
                week < nextRegularWeeks;
                week++
            ) {
                if (!nextGames[String(week)]) {
                    nextGames[String(week)] = [];
                }
            }

            return nextGames;
        });

        setByesByWeek(function (currentByes) {
            const nextByes: Record<string, string[]> = {};

            Object.keys(currentByes).forEach(function (weekKey) {
                if (Number(weekKey) < nextRegularWeeks) {
                    nextByes[weekKey] = currentByes[weekKey];
                }
            });

            /*
                Las semanas nuevas comienzan con todos los equipos
                en bye hasta que el comisionado agregue partidos.
            */
            for (
                let week = 0;
                week < nextRegularWeeks;
                week++
            ) {
                if (!nextByes[String(week)]) {
                    nextByes[String(week)] = slots.map(
                        function (slot) {
                            return slot.slot;
                        }
                    );
                }
            }

            return nextByes;
        });
    }

    function addGame(week: number) {
        if (!isWeekEditable(week)) {
            return;
        }

        const usedSlots = getUsedSlotsInWeek(week);

        const availableSlots = slots
            .map(function (slot) {
                return slot.slot;
            })
            .filter(function (slot) {
                return !usedSlots.includes(slot);
            });

        if (availableSlots.length < 2) {
            setToastMessage(
                "No hay dos equipos disponibles para agregar otro partido."
            );

            return;
        }

        const localSlot = availableSlots[0];
        const visitanteSlot = availableSlots[1];

        setGamesByWeek(function (currentGames) {
            return {
                ...currentGames,
                [String(week)]: [
                    ...getWeekGames(currentGames, week),
                    {
                        id: createTemporaryGameId(),
                        localSlot,
                        visitanteSlot
                    }
                ]
            };
        });

        /*
            Al crear un juego, ambos equipos dejan de tener bye.
        */
        setByesByWeek(function (currentByes) {
            return {
                ...currentByes,
                [String(week)]: getWeekByes(
                    currentByes,
                    week
                ).filter(function (slot) {
                    return (
                        slot !== localSlot &&
                        slot !== visitanteSlot
                    );
                })
            };
        });
    }

    function removeGame(
        week: number,
        gameId: string
    ) {
        if (!isWeekEditable(week)) {
            return;
        }

        const currentGame = getWeekGames(
            gamesByWeek,
            week
        ).find(function (game) {
            return game.id === gameId;
        });

        if (!currentGame) {
            return;
        }

        setGamesByWeek(function (currentGames) {
            return {
                ...currentGames,
                [String(week)]: getWeekGames(
                    currentGames,
                    week
                ).filter(function (game) {
                    return game.id !== gameId;
                })
            };
        });

        /*
            Al quitar un partido, sus equipos pasan inicialmente
            a bye. El comisionado puede crearles otro juego después.
        */
        setByesByWeek(function (currentByes) {
            const currentWeekByes = getWeekByes(
                currentByes,
                week
            );

            return {
                ...currentByes,
                [String(week)]: Array.from(
                    new Set([
                        ...currentWeekByes,
                        currentGame.localSlot,
                        currentGame.visitanteSlot
                    ])
                )
            };
        });
    }

    function changeGameSlot(
        week: number,
        gameId: string,
        field: "localSlot" | "visitanteSlot",
        value: unknown
    ) {
        if (!isWeekEditable(week)) {
            return;
        }

        const selectedSlot = String(value || "");

        if (!slots.some(function (slot) {
            return slot.slot === selectedSlot;
        })) {
            return;
        }

        const currentGame = getWeekGames(
            gamesByWeek,
            week
        ).find(function (game) {
            return game.id === gameId;
        });

        if (!currentGame) {
            return;
        }

        const otherField =
            field === "localSlot"
                ? "visitanteSlot"
                : "localSlot";

        if (selectedSlot === currentGame[otherField]) {
            setToastMessage(
                "Un equipo no puede jugar contra sí mismo."
            );

            return;
        }

        const slotIsUsedByAnotherGame = getWeekGames(
            gamesByWeek,
            week
        ).some(function (game) {
            if (game.id === gameId) {
                return false;
            }

            return (
                game.localSlot === selectedSlot ||
                game.visitanteSlot === selectedSlot
            );
        });

        if (slotIsUsedByAnotherGame) {
            setToastMessage(
                "Este equipo ya tiene un partido en esta jornada."
            );

            return;
        }

        const previousSlot = currentGame[field];

        setGamesByWeek(function (currentGames) {
            return {
                ...currentGames,
                [String(week)]: getWeekGames(
                    currentGames,
                    week
                ).map(function (game) {
                    if (game.id !== gameId) {
                        return game;
                    }

                    return {
                        ...game,
                        [field]: selectedSlot
                    };
                })
            };
        });

        setByesByWeek(function (currentByes) {
            const withoutSelectedSlot = getWeekByes(
                currentByes,
                week
            ).filter(function (slot) {
                return slot !== selectedSlot;
            });

            return {
                ...currentByes,
                [String(week)]: Array.from(
                    new Set([
                        ...withoutSelectedSlot,
                        previousSlot
                    ])
                )
            };
        });
    }

    function toggleBye(
        week: number,
        slot: string
    ) {
        if (!isWeekEditable(week)) {
            return;
        }

        const usedSlots = getUsedSlotsInWeek(week);

        if (usedSlots.includes(slot)) {
            setToastMessage(
                "No puedes poner en bye a un equipo que ya tiene partido."
            );

            return;
        }

        setByesByWeek(function (currentByes) {
            const currentWeekByes = getWeekByes(
                currentByes,
                week
            );

            const hasBye = currentWeekByes.includes(slot);

            return {
                ...currentByes,
                [String(week)]: hasBye
                    ? currentWeekByes.filter(function (item) {
                          return item !== slot;
                      })
                    : [...currentWeekByes, slot]
            };
        });
    }

    function validateBeforeSave(): boolean {
        for (
            let week = 0;
            week < regularWeeks;
            week++
        ) {
            const games = getWeekGames(
                gamesByWeek,
                week
            );

            const byes = getWeekByes(
                byesByWeek,
                week
            );

            const usedSlots: string[] = [];

            for (const game of games) {
                if (
                    !game.localSlot ||
                    !game.visitanteSlot ||
                    game.localSlot === game.visitanteSlot
                ) {
                    setToastMessage(
                        `Revisa los equipos de la jornada ${
                            week + 1
                        }.`
                    );

                    return false;
                }

                if (
                    usedSlots.includes(game.localSlot) ||
                    usedSlots.includes(game.visitanteSlot)
                ) {
                    setToastMessage(
                        `Un equipo tiene más de un juego en la jornada ${
                            week + 1
                        }.`
                    );

                    return false;
                }

                usedSlots.push(
                    game.localSlot,
                    game.visitanteSlot
                );
            }

            for (const slot of slots) {
                const plays = usedSlots.includes(slot.slot);
                const hasBye = byes.includes(slot.slot);

                if (!plays && !hasBye) {
                    setToastMessage(
                        `${slot.teamName} debe tener partido o bye en la jornada ${
                            week + 1
                        }.`
                    );

                    return false;
                }

                if (plays && hasBye) {
                    setToastMessage(
                        `${slot.teamName} no puede jugar y tener bye en la misma jornada.`
                    );

                    return false;
                }
            }
        }

        return true;
    }

    async function saveSchedule() {
        if (saving || !league) {
            return;
        }

        if (!validateBeforeSave()) {
            return;
        }

        const games = Object.entries(gamesByWeek).flatMap(
            function ([weekKey, weekGames]) {
                return weekGames.map(function (game) {
                    return {
                        id: game.id,
                        jornada: Number(weekKey),
                        localSlot: game.localSlot,
                        visitanteSlot: game.visitanteSlot
                    };
                });
            }
        );

        try {
            setSaving(true);

const result = await fantasyApiPost<
    FantasyScheduleResponse
>(
    "control_FantasySchedule.php",
    {
        leagueId: id,
        playoffs,
        regularWeeks,
        games,
        byes: byesByWeek
    }
);

            setToastMessage(
                result.message ||
                    "Calendario guardado correctamente."
            );

            await loadSchedule();
        } catch (saveError) {
            console.error(
                "No fue posible guardar el calendario:",
                saveError
            );

            setToastMessage(
                saveError instanceof Error
                    ? saveError.message
                    : "No fue posible guardar el calendario."
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="schedule-settings-toolbar">
                    <IonButton
                        slot="start"
                        fill="clear"
                        color="medium"
                        aria-label="Regresar a configuración"
                        onClick={goBack}
                    >
                        <IonIcon icon={closeOutline} />
                    </IonButton>

                    <IonTitle>Calendario de liga</IonTitle>

                    <IonButton
                        slot="end"
                        fill="clear"
                        disabled={loading || saving || Boolean(error)}
                        aria-label="Guardar calendario"
                        onClick={saveSchedule}
                    >
                        <IonIcon icon={saveOutline} />
                    </IonButton>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="schedule-settings-content"
            >
                {loading ? (
                    <IonProgressBar
                        className="schedule-settings-progress"
                        type="indeterminate"
                    />
                ) : null}

                {!loading && error ? (
                    <main className="schedule-settings-main">
                        <section className="schedule-settings-empty">
                            <strong>
                                No pudimos cargar el calendario
                            </strong>

                            <span>{error}</span>

                            <IonButton
                                size="small"
                                fill="outline"
                                onClick={loadSchedule}
                            >
                                Reintentar
                            </IonButton>
                        </section>
                    </main>
                ) : null}

                {!loading && !error && league ? (
                    <main className="schedule-settings-main">
                        <section className="schedule-settings-hero">
                            <span>COMISIONADO</span>

                            <h1>{league.name}</h1>

                            <p>
                                Las jornadas que ya iniciaron quedan bloqueadas.
                                Puedes ajustar enfrentamientos y descansos futuros.
                            </p>
                        </section>

                        <section className="schedule-settings-notice">
                            <strong>
                                Jornada real inicial:{" "}
                                {league.firstRealWeek}
                            </strong>

                            <span>
                                Hay {league.availableWeeks} jornadas
                                reales disponibles para temporada regular
                                y playoffs.
                            </span>
                        </section>

                        <section className="schedule-settings-section">
                            <span className="schedule-settings-label">
                                FORMATO DE TEMPORADA
                            </span>

                            <div className="schedule-settings-card">
                                <label>
                                    <span>Equipos en playoffs</span>

                                    <IonSelect
                                        value={playoffs}
                                        interface="action-sheet"
                                        disabled={saving}
                                        onIonChange={function (event) {
                                            changePlayoffs(
                                                event.detail.value
                                            );
                                        }}
                                    >
                                        {playoffOptions.map(
                                            function (option) {
                                                return (
                                                    <IonSelectOption
                                                        key={option}
                                                        value={option}
                                                    >
                                                        {option === 0
                                                            ? "Sin playoffs"
                                                            : `${option} equipos`}
                                                    </IonSelectOption>
                                                );
                                            }
                                        )}
                                    </IonSelect>
                                </label>

                                <label>
                                    <span>
                                        Semanas de temporada regular
                                    </span>

                                    <IonSelect
                                        value={regularWeeks}
                                        interface="action-sheet"
                                        disabled={saving}
                                        onIonChange={function (event) {
                                            changeRegularWeeks(
                                                event.detail.value
                                            );
                                        }}
                                    >
                                        {regularWeekOptions.map(
                                            function (week) {
                                                return (
                                                    <IonSelectOption
                                                        key={week}
                                                        value={week}
                                                    >
                                                        {week}{" "}
                                                        {week === 1
                                                            ? "semana"
                                                            : "semanas"}
                                                    </IonSelectOption>
                                                );
                                            }
                                        )}
                                    </IonSelect>
                                </label>
                            </div>

                            <p className="schedule-settings-help">
                                {playoffWeeks === 0
                                    ? "No se reservarán jornadas para playoffs."
                                    : `Se reservarán ${playoffWeeks} ${
                                          playoffWeeks === 1
                                              ? "jornada"
                                              : "jornadas"
                                      } real${
                                          playoffWeeks === 1
                                              ? ""
                                              : "es"
                                      } para playoffs.`}
                            </p>
                        </section>

                        {Array.from(
                            { length: regularWeeks },
                            function (_, week) {
                                const weekGames = getWeekGames(
                                    gamesByWeek,
                                    week
                                );

                                const weekByes = getWeekByes(
                                    byesByWeek,
                                    week
                                );

                                const editable = isWeekEditable(
                                    week
                                );

                                return (
                                    <section
                                        className="schedule-settings-section"
                                        key={week}
                                    >
                                        <div className="schedule-settings-week-heading">
                                            <div>
                                                <span className="schedule-settings-label">
                                                    JORNADA FANTASY{" "}
                                                    {week + 1}
                                                </span>

                                                <p>
                                                    Corresponde a la jornada
                                                    real{" "}
                                                    {league.firstRealWeek +
                                                        week}
                                                    .
                                                </p>
                                            </div>

                                            {!editable ? (
                                                <span className="schedule-settings-locked">
                                                    Jornada iniciada
                                                </span>
                                            ) : null}
                                        </div>

                                        <div className="schedule-settings-week">
                                            {weekGames.map(
                                                function (game) {
                                                    return (
                                                        <article
                                                            className="schedule-settings-game"
                                                            key={game.id}
                                                        >
                                                            <IonSelect
                                                                value={
                                                                    game.localSlot
                                                                }
                                                                interface="action-sheet"
                                                                disabled={
                                                                    !editable ||
                                                                    saving
                                                                }
                                                                onIonChange={function (
                                                                    event
                                                                ) {
                                                                    changeGameSlot(
                                                                        week,
                                                                        game.id,
                                                                        "localSlot",
                                                                        event
                                                                            .detail
                                                                            .value
                                                                    );
                                                                }}
                                                            >
                                                                {slots.map(
                                                                    function (
                                                                        slot
                                                                    ) {
                                                                        return (
                                                                            <IonSelectOption
                                                                                key={
                                                                                    slot.slot
                                                                                }
                                                                                value={
                                                                                    slot.slot
                                                                                }
                                                                            >
                                                                                {
                                                                                    slot.teamName
                                                                                }
                                                                            </IonSelectOption>
                                                                        );
                                                                    }
                                                                )}
                                                            </IonSelect>

                                                            <span>vs.</span>

                                                            <IonSelect
                                                                value={
                                                                    game.visitanteSlot
                                                                }
                                                                interface="action-sheet"
                                                                disabled={
                                                                    !editable ||
                                                                    saving
                                                                }
                                                                onIonChange={function (
                                                                    event
                                                                ) {
                                                                    changeGameSlot(
                                                                        week,
                                                                        game.id,
                                                                        "visitanteSlot",
                                                                        event
                                                                            .detail
                                                                            .value
                                                                    );
                                                                }}
                                                            >
                                                                {slots.map(
                                                                    function (
                                                                        slot
                                                                    ) {
                                                                        return (
                                                                            <IonSelectOption
                                                                                key={
                                                                                    slot.slot
                                                                                }
                                                                                value={
                                                                                    slot.slot
                                                                                }
                                                                            >
                                                                                {
                                                                                    slot.teamName
                                                                                }
                                                                            </IonSelectOption>
                                                                        );
                                                                    }
                                                                )}
                                                            </IonSelect>

                                                            <button
                                                                type="button"
                                                                aria-label="Eliminar partido"
                                                                disabled={
                                                                    !editable ||
                                                                    saving
                                                                }
                                                                onClick={function () {
                                                                    removeGame(
                                                                        week,
                                                                        game.id
                                                                    );
                                                                }}
                                                            >
                                                                <IonIcon
                                                                    icon={
                                                                        removeOutline
                                                                    }
                                                                />
                                                            </button>
                                                        </article>
                                                    );
                                                }
                                            )}

                                            {editable ? (
                                                <button
                                                    type="button"
                                                    className="schedule-settings-add"
                                                    disabled={saving}
                                                    onClick={function () {
                                                        addGame(week);
                                                    }}
                                                >
                                                    <IonIcon
                                                        icon={addOutline}
                                                    />

                                                    Agregar partido
                                                </button>
                                            ) : null}

                                            <div className="schedule-settings-byes">
                                                <strong>
                                                    Jornada de descanso
                                                </strong>

                                                <div>
                                                    {slots.map(
                                                        function (slot) {
                                                            const hasBye =
                                                                weekByes.includes(
                                                                    slot.slot
                                                                );

                                                            const hasGame =
                                                                getUsedSlotsInWeek(
                                                                    week
                                                                ).includes(
                                                                    slot.slot
                                                                );

                                                            return (
                                                                <button
                                                                    type="button"
                                                                    key={
                                                                        slot.slot
                                                                    }
                                                                    disabled={
                                                                        !editable ||
                                                                        saving ||
                                                                        hasGame
                                                                    }
                                                                    className={
                                                                        hasBye
                                                                            ? "schedule-settings-bye schedule-settings-bye--selected"
                                                                            : "schedule-settings-bye"
                                                                    }
                                                                    onClick={function () {
                                                                        toggleBye(
                                                                            week,
                                                                            slot.slot
                                                                        );
                                                                    }}
                                                                >
                                                                    {
                                                                        slot.teamName
                                                                    }
                                                                </button>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                );
                            }
                        )}

                        <IonButton
                            expand="block"
                            className="schedule-settings-save"
                            disabled={saving}
                            onClick={saveSchedule}
                        >
                            {saving
                                ? "Guardando calendario..."
                                : "Guardar calendario"}

                            <IonIcon
                                slot="end"
                                icon={saveOutline}
                            />
                        </IonButton>
                    </main>
                ) : null}
            </IonContent>

            <IonToast
                isOpen={Boolean(toastMessage)}
                message={toastMessage}
                duration={3200}
                position="bottom"
                onDidDismiss={function () {
                    setToastMessage("");
                }}
            />
        </IonPage>
    );
};

export default FantasyScheduleSettings;